import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Pagination,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from "@mui/material";
import {
  FaCheckDouble,
  FaBell,
  FaShoppingBag,
  FaBoxOpen,
  FaUserShield,
  FaExclamationCircle,
  FaInfoCircle,
  FaSync
} from "react-icons/fa";
import { getData, patchData } from "../../Webservice/Webservice";

const adminColors = {
  primary: "#131921", // Amazon Dark
  secondary: "#232F3E",
  accent: "#FF9900", // Amazon Orange
  unread: "#F7FAFF",
  border: "#D5D9D9",
  critical: "#C40000",
  high: "#B12704",
  success: "#007600",
  text: "#0F1111"
};

const fontStyle = {
  fontFamily: '"Amazon Ember", "Mulish", sans-serif'
};

function Notification() {
  const navigate = useNavigate();
  const storedRole = (sessionStorage.getItem("userType") || "").toLowerCase();
  const isVendor = storedRole === "vendor";

  const [activeTab, setActiveTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [availableTypes, setAvailableTypes] = useState([]);

  const PAGE_SIZE = 15;
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });

  const getCategorizedIcon = (type) => {
    switch (type) {
      case 'order': return <FaShoppingBag style={{ color: adminColors.accent }} />;
      case 'product': return <FaBoxOpen style={{ color: "#007185" }} />;
      case 'vendor':
      case 'vendor_request': return <FaUserShield style={{ color: "#565959" }} />;
      default: return <FaBell style={{ color: "#565959" }} />;
    }
  };

  const fetchNotifications = async (page = 1, showLoading = true) => {
    try {
      if (showLoading) setLoading(true);

      const commonToken = sessionStorage.getItem('commonToken');
      const sessionToken = sessionStorage.getItem('token');
      const token = `Bearer ${commonToken || sessionToken}`;

      const userModel = isVendor ? 'Seller' : 'User';
      const endpoint = isVendor ? "vendor/notification/vend" : "notification/admin";

      let queryParams = `page=${page}&limit=${PAGE_SIZE}`;
      if (activeTab === "unread") queryParams += "&isRead=false";
      else if (activeTab !== "all") queryParams += `&type=${activeTab}`;

      const [notifResponse, countResponse] = await Promise.all([
        getData(`${endpoint}?${queryParams}`, null, token),
        getData(`notification/unread-count?userModel=${userModel}`, null, token)
      ]);

      if (notifResponse.success) {
        setNotifications(notifResponse.data.map(n => ({
          id: n._id,
          title: n.title,
          message: n.message,
          time: formatRelativeTime(n.createdAt),
          type: n.type || "system",
          unread: !n.isRead,
          priority: n.priority || "medium",
          linkId: n.linkId
        })));
        setMeta({
          page: notifResponse.meta?.page || 1,
          totalPages: notifResponse.meta?.totalPages || 1,
        });

        // Dynamic tabs
        const types = [...new Set(notifResponse.data.map(n => n.type))].filter(Boolean);
        setAvailableTypes(prev => [...new Set([...prev, ...types])]);
      }
      if (countResponse.success) setUnreadCount(countResponse.unreadCount);
    } catch (err) {
      setError("Error loading notifications");
    } finally {
      setLoading(false);
    }
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  useEffect(() => {
    fetchNotifications(meta.page);
    const interval = setInterval(() => fetchNotifications(meta.page, false), 15000);
    return () => clearInterval(interval);
  }, [meta.page, activeTab]);

  const markAsRead = async (id) => {
    try {
      const commonToken = sessionStorage.getItem('commonToken');
      const sessionToken = sessionStorage.getItem('token');
      const token = `Bearer ${commonToken || sessionToken}`;

      setNotifications(prev => prev.map(n => n.id === id ? { ...n, unread: false } : n));
      await patchData(`notification/read/${id}`, {}, token);
      if (window.refreshNotificationCount) window.refreshNotificationCount();
    } catch (err) { console.error(err); }
  };

  const markAllRead = async () => {
    try {
      const commonToken = sessionStorage.getItem('commonToken');
      const sessionToken = sessionStorage.getItem('token');
      const token = `Bearer ${commonToken || sessionToken}`;
      const userModel = isVendor ? 'Seller' : 'User';

      await patchData(`notification/mark-all-read?userModel=${userModel}`, {}, token);
      fetchNotifications(1, false);
      if (window.refreshNotificationCount) window.refreshNotificationCount();
    } catch (err) { console.error(err); }
  };

  const handleNotificationClick = (n) => {
    if (n.unread) markAsRead(n.id);

    const routes = {
      order: `/ordermanagement/${n.linkId}`,
      coupon: `/couponsmanagement/${n.linkId}`,
      vendor_request: "/productrequestlist",
      product: "/productmanagement",
      vendor: "/vendormanagement",
      vendor_order: `/vendor/onlineorders/${n.linkId}`
    };
    if (routes[n.type]) navigate(routes[n.type]);
  };

  const tabs = [
    { label: "All", value: "all" },
    { label: "Unread", value: "unread" },
    ...availableTypes.map(t => ({ label: t.charAt(0).toUpperCase() + t.slice(1).replace('_', ' '), value: t }))
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, margin: "0 auto", ...fontStyle }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: adminColors.primary }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip label={`${unreadCount} New`} size="small" sx={{ bgcolor: adminColors.accent, color: "#fff", fontWeight: 700 }} />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchNotifications()} size="small">
              <FaSync size={16} />
            </IconButton>
          </Tooltip>
          <Button
            startIcon={<FaCheckDouble />}
            onClick={markAllRead}
            size="small"
            disabled={unreadCount === 0}
            sx={{ textTransform: "none", color: "#007185", fontWeight: 600 }}
          >
            Mark all as read
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: adminColors.border,
          '& .MuiTabs-indicator': { bgcolor: adminColors.accent },
          '& .MuiTab-root': { textTransform: "none", fontWeight: 600, color: "#565959", '&.Mui-selected': { color: adminColors.accent } }
        }}
      >
        {tabs.map(t => <Tab key={t.value} label={t.label} value={t.value} />)}
      </Tabs>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: adminColors.accent }} /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Stack spacing={0}>
          {notifications.map((n, index) => (
            <React.Fragment key={n.id}>
              <Card
                onClick={() => handleNotificationClick(n)}
                sx={{
                  boxShadow: "none",
                  borderRadius: 0,
                  bgcolor: n.unread ? adminColors.unread : "transparent",
                  cursor: "pointer",
                  transition: "background 0.2s",
                  "&:hover": { bgcolor: n.unread ? "#F0F2F2" : "#F7F8F8" }
                }}
              >
                <CardContent sx={{ p: "16px !important", display: "flex", gap: 2 }}>
                  <Box sx={{ pt: 0.5 }}>{getCategorizedIcon(n.type)}</Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: n.unread ? 700 : 500, fontSize: "14px", color: adminColors.text }}>
                        {n.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#565959" }}>{n.time}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#565959", mt: 0.5, fontSize: "13px" }}>
                      {n.message}
                    </Typography>
                    <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label={n.type.replace('_', ' ')} size="small" sx={{ height: 18, fontSize: "11px", textTransform: "capitalize", bgcolor: "#F0F2F2", color: "#565959" }} />
                      {n.priority === 'critical' && (
                        <Typography variant="caption" sx={{ color: adminColors.critical, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5 }}>
                          <FaExclamationCircle size={10} /> Urgent
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {n.unread && <Box sx={{ width: 8, height: 8, bgcolor: adminColors.accent, borderRadius: "50%", mt: 1.5 }} />}
                </CardContent>
              </Card>
              <Divider />
            </React.Fragment>
          ))}

          {notifications.length === 0 && (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <FaBell size={48} color="#D5D9D9" />
              <Typography sx={{ mt: 2, color: "#565959" }}>No notifications in this category</Typography>
            </Box>
          )}

          {meta.totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={meta.totalPages}
                page={meta.page}
                onChange={(e, p) => setMeta(prev => ({ ...prev, page: p }))}
                shape="rounded"
                color="primary"
              />
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}

export default Notification;
